'use client';

import React, { FC, useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FormTextField } from '@/components/common/FormComponents';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { useDialogStore, type BulkShippingDialogResult, type BulkShippingSampleWithPiece } from '@/stores/dialogStore';
import { useBackendStore } from '@/stores/backendStore';
import { useAuthStore } from '@/stores/authStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import type { BulkSampleRowType } from '@/components/tables/bulk/BulkSampleGrid';
import { ShippingTypeSelect } from '@/components/details/shipping/ShippingTypeSelect';

interface ShippingSampleLine {
  index: number;
  sampleId: number;
  sampleLabel: string;
  material?: string;
  description?: string;
  date: string;
  /** Existing piece count from bulk grid (0 = unsplit). */
  existingPieces: number;
  pieceInput: string;
  pieceError?: string;
}

// Helper functions for piece number parsing (from Knockout implementation)
const createRange = (start: number, end: number) => Array.from(Array(end - start + 1).keys()).map((i) => i + start);

const convertToNumber = (nbEntered: string): number => {
  return isNaN(parseInt(nbEntered, 10)) ? lettersToNumbers(nbEntered) : parseInt(nbEntered, 10);
};

const lettersToNumbers = (input: string): number => {
  const nbs = input.split('').map((s) => parseInt(s, 36) - 9);
  return nbs.reverse().reduce((p, c, i) => p + c * Math.pow(26, i));
};

const rangeToArray = (range: string): number[] => {
  const fromTo = range.split('-').map((e) => convertToNumber(e.trim()));
  if (fromTo.length < 2) {
    return fromTo;
  }
  if (fromTo[0] >= fromTo[1]) {
    throw new Error('Please use correct range [from-to], the first number has to be less than the second');
  }
  return createRange(fromTo[0], fromTo[1]);
};

const getPieceNumbers = (input: string): number[] => {
  if (!input || !input.trim()) {
    return [];
  }
  return input
    .split(',')
    .map((i) => i.trim())
    .filter(Boolean)
    .map((v) => rangeToArray(v))
    .flat();
};

/**
 * Dialog for creating shipping requests with piece selection
 * Based on CreateShippingPopup from Knockout implementation
 */
export const CreateShippingDialog: FC = () => {
  const { bulkShippingDialog, closeBulkShippingDialog } = useDialogStore();
  const getBackend = useBackendStore((s) => s.getBackend);
  const { currentUser } = useAuthStore();
  const { showSnackbar } = useSnackbar();
  const { hasUnsavedChanges, markAsInteracted } = useUserInteraction();

  const { samples: initialSamples, projectLabel } = bulkShippingDialog;
  const isOpen = bulkShippingDialog.isOpen;

  // Initialize sample lines
  const initialSampleLines: ShippingSampleLine[] = useMemo(() => {
    if (!initialSamples || initialSamples.length === 0) return [];
    return initialSamples.map((s, idx) => {
      // Cast to BulkSampleRowType to access growth property
      const sample = s as unknown as BulkSampleRowType;
      const growthDate = sample.growth?.date;
      return {
        index: idx + 1,
        sampleId: s.id,
        sampleLabel: s.sampleLabel || '',
        material: s.material || '', // material is a string in BulkSampleDto
        description: s.description || '',
        date: growthDate ? new Date(growthDate).toLocaleDateString() : '',
        existingPieces: s.pieces ?? 0,
        pieceInput: '',
        pieceError: undefined,
      };
    });
  }, [initialSamples]);

  const [sampleLines, setSampleLines] = useState<ShippingSampleLine[]>(initialSampleLines);
  const [isLocalTransfer, setIsLocalTransfer] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');
  const [sampleLabelToAdd, setSampleLabelToAdd] = useState<string>('');
  const [tab, setTab] = useState(0);
  const [returnAddress, setReturnAddress] = useState('');
  const [researcherPhone, setResearcherPhone] = useState('');
  const [budget, setBudget] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [address, setAddress] = useState('');
  const [shippingType, setShippingType] = useState('');
  const [shippingTypeOptions, setShippingTypeOptions] = useState<string[]>([]);
  const [transferDate, setTransferDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Reset table to passed-in samples when dialog opens with (possibly new) samples.
  // Per https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [lastOpenKey, setLastOpenKey] = useState<string | null>(null);
  const openKey = isOpen ? `open-${initialSamples?.map((s) => s.id).join(',') ?? ''}` : null;
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey);
    if (isOpen) {
      setSampleLines(initialSampleLines);
      setTab(0); // Always open on first tab (Select Samples)
    }
  }

  // Fetch return address and shipping types when user navigates to Enter Recipient tab
  useEffect(() => {
    if (!isOpen || tab !== 1) return;
    const backend = getBackend();
    if (!returnAddress) {
      backend.shipping
        .getReturnAddress()
        .then((addr) => setReturnAddress(addr || ''))
        .catch((err) => log.error('[CreateShippingDialog] Failed to fetch return address:', err));
    }
    if (shippingTypeOptions.length === 0) {
      backend.shipping
        .getShippingTypes()
        .then((types) => setShippingTypeOptions(types ?? []))
        .catch((err) => log.error('[CreateShippingDialog] Failed to fetch shipping types:', err));
    }
  }, [isOpen, tab, returnAddress, shippingTypeOptions.length, getBackend]);

  // Validate piece input (format only; empty is allowed for ship-whole)
  const validatePieceInput = useCallback((input: string): string | undefined => {
    if (!input || !input.trim()) {
      return undefined;
    }

    const trimmed = input.trim();
    // Reject long numeric-only strings that look like sample IDs
    if (trimmed.length >= 5 && /^\d+$/.test(trimmed)) {
      return "This doesn't look like a sample ID";
    }

    try {
      getPieceNumbers(trimmed);
      return undefined;
    } catch (error: any) {
      return error.message || 'Invalid piece number format';
    }
  }, []);

  // Handle piece input change
  const handlePieceInputChange = useCallback(
    (index: number, value: string) => {
      setSampleLines((prev) =>
        prev.map((line) => {
          if (line.index === index) {
            const error = validatePieceInput(value);
            return { ...line, pieceInput: value, pieceError: error };
          }
          return line;
        })
      );
      markAsInteracted();
    },
    [validatePieceInput, markAsInteracted]
  );

  // Remove sample from list
  const handleRemoveSample = useCallback(
    (index: number) => {
      setSampleLines((prev) =>
        prev.filter((line) => line.index !== index).map((line, idx) => ({ ...line, index: idx + 1 }))
      );
      markAsInteracted();
    },
    [markAsInteracted]
  );

  // Add sample to list
  const handleAddSample = useCallback(async () => {
    if (!sampleLabelToAdd || !sampleLabelToAdd.trim()) {
      showSnackbar('Please enter a sample label.', 'error');
      return;
    }

    // Check if it's numeric (we exclude numeric inputs)
    if (isFinite(+sampleLabelToAdd.trim())) {
      showSnackbar("This doesn't look like a sample ID", 'error');
      return;
    }

    try {
      // Get sample by label - need to search for it
      // For now, show error - sample lookup would need to be implemented
      showSnackbar(
        'Sample lookup by label is not yet implemented in the new API client. Please select samples from the grid.',
        'warning'
      );
      return;
    } catch (error: any) {
      if (error?.status === 404) {
        showSnackbar(`No sample with ID ${sampleLabelToAdd} found`, 'error');
      } else {
        log.error('[CreateShippingDialog] Error fetching sample:', error);
        showSnackbar(getProblemDetail(error) ?? 'Failed to fetch sample', 'error');
      }
    }
  }, [sampleLabelToAdd, showSnackbar]);

  // Reset and close - reset to initial state
  const handleClose = useCallback(() => {
    // Reset to fresh initial state based on current initialSamples
    const freshLines: ShippingSampleLine[] =
      initialSamples && initialSamples.length > 0
        ? initialSamples.map((s, idx) => {
            const sample = s as unknown as BulkSampleRowType;
            const growthDate = sample.growth?.date;
            return {
              index: idx + 1,
              sampleId: s.id,
              sampleLabel: s.sampleLabel || '',
              material: s.material || '',
              description: s.description || '',
              date: growthDate ? new Date(growthDate).toLocaleDateString() : '',
              existingPieces: s.pieces ?? 0,
              pieceInput: '',
              pieceError: undefined,
            };
          })
        : [];
    setSampleLines(freshLines);
    setIsLocalTransfer(false);
    setComment('');
    setSampleLabelToAdd('');
    setTab(0); // Always return to first tab for next open
    // Keep recipient fields (returnAddress, researcherPhone, budget, contactName, etc.) for convenience
    closeBulkShippingDialog(null);
  }, [initialSamples, closeBulkShippingDialog]);

  /** Validates piece inputs and ensures at least one sample is included. Returns true if valid. */
  const validatePiecesForNextOrSubmit = useCallback((): boolean => {
    let hasErrors = false;
    const updatedLines = sampleLines.map((line) => {
      const error = validatePieceInput(line.pieceInput);
      if (error) hasErrors = true;
      return { ...line, pieceError: error };
    });
    if (hasErrors) {
      setSampleLines(updatedLines);
      showSnackbar('Please correct piece number errors before continuing.', 'error');
      return false;
    }

    const splitWithoutPieces = sampleLines.filter((line) => !line.pieceInput.trim() && line.existingPieces > 0);
    if (splitWithoutPieces.length > 0) {
      showSnackbar(`Sample ${splitWithoutPieces[0].sampleLabel} was split; specify which pieces to ship.`, 'error');
      return false;
    }

    const hasAnyRow = sampleLines.some((line) => line.pieceInput.trim().length > 0 || line.existingPieces === 0);
    if (!hasAnyRow) {
      showSnackbar('Select at least one sample to ship before continuing.', 'error');
      return false;
    }

    return true;
  }, [sampleLines, validatePieceInput, showSnackbar]);

  const handleGoToRecipientTab = useCallback(() => {
    if (validatePiecesForNextOrSubmit()) setTab(1);
  }, [validatePiecesForNextOrSubmit]);

  const handleSubmit = useCallback(() => {
    if (!validatePiecesForNextOrSubmit()) return;

    // Collect sample rows (piece spec strings; server resolves ranges)
    const samplesWithPieces: BulkShippingSampleWithPiece[] = [];
    for (const line of sampleLines) {
      const trimmed = line.pieceInput.trim();
      if (trimmed) {
        samplesWithPieces.push({ sampleId: line.sampleId, pieceSpec: trimmed });
      } else if (line.existingPieces === 0) {
        samplesWithPieces.push({ sampleId: line.sampleId });
      }
    }

    const result: BulkShippingDialogResult = {
      samples: samplesWithPieces,
      isLocalTransfer,
      comment: comment || undefined,
      ...(isLocalTransfer
        ? { transferDate }
        : {
            returnAddress: returnAddress || undefined,
            shippingType: shippingType || undefined,
            researcherPhone: researcherPhone || undefined,
            budget: budget || undefined,
            contactName: contactName || undefined,
            contactEmail: contactEmail || undefined,
            contactPhone: contactPhone || undefined,
            address: address || undefined,
            researcher: currentUser?.id != null && !isNaN(Number(currentUser.id)) ? Number(currentUser.id) : undefined,
            researcherEmail: currentUser?.email || currentUser?.eppn || undefined,
          }),
    };

    setTab(0); // Reset so dialog always opens on first tab next time
    closeBulkShippingDialog(result);
  }, [
    sampleLines,
    isLocalTransfer,
    comment,
    returnAddress,
    shippingType,
    transferDate,
    researcherPhone,
    budget,
    contactName,
    contactEmail,
    contactPhone,
    address,
    currentUser,
    validatePiecesForNextOrSubmit,
    closeBulkShippingDialog,
  ]);

  const showRecipientTab = !isLocalTransfer;
  const isNextStep = tab === 0 && showRecipientTab;
  const canSubmitRecipient =
    budget.trim().length > 0 &&
    shippingType.trim().length > 0 &&
    researcherPhone.trim().length > 0 &&
    returnAddress.trim().length > 0 &&
    contactName.trim().length > 0 &&
    contactEmail.trim().length > 0 &&
    contactPhone.trim().length > 0 &&
    address.trim().length > 0;
  const submitDisabled = tab === 1 && !canSubmitRecipient;

  return (
    <StandardDialog
      open={isOpen}
      onClose={handleClose}
      title="Which pieces are to be shipped?"
      helpDocKey="dialog.createShipping"
      maxWidth="lg"
      onSubmit={isNextStep ? handleGoToRecipientTab : handleSubmit}
      submitText={isNextStep ? 'Next' : 'Split samples & create Shipping'}
      submitDisabled={submitDisabled}
      hasUnsavedChanges={hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes. Are you sure you want to discard them?"
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Project <strong>{projectLabel}</strong>
        </Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tab label="Select Samples" />
          <Tab label="Enter Recipient" disabled={isLocalTransfer} />
        </Tabs>

        {tab === 0 ? (
          <>
            {/* Sample table */}
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Sample</TableCell>
                  <TableCell>Material</TableCell>
                  <TableCell>Growth Date</TableCell>
                  <TableCell>Piece numbers (or letters) to ship</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sampleLines.map((line) => (
                  <React.Fragment key={line.index}>
                    <TableRow>
                      <TableCell>{line.index}</TableCell>
                      <TableCell>{line.sampleLabel}</TableCell>
                      <TableCell>
                        <Typography variant="body2" dangerouslySetInnerHTML={{ __html: line.material || '' }} />
                      </TableCell>
                      <TableCell>{line.date}</TableCell>
                      <TableCell>
                        <FormTextField
                          value={line.pieceInput}
                          onChange={(e) => handlePieceInputChange(line.index, e.target.value)}
                          placeholder="blank = ship whole; or 1-3, A,B"
                          fullWidth
                          size="small"
                          error={!!line.pieceError}
                          helperText={
                            line.pieceError ||
                            (line.existingPieces > 0
                              ? 'range (C-F or 3-6), or comma separated numbers'
                              : 'blank = ship entire sample; or range/list of pieces')
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleRemoveSample(line.index)}
                          startIcon={<DeleteIcon />}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                    {line.description ? (
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" dangerouslySetInnerHTML={{ __html: line.description }} />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                ))}
                {/* Add sample row – aligned with columns */}
                <TableRow>
                  <TableCell sx={{ width: 48, verticalAlign: 'middle' }} />
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    <FormTextField
                      label="Add Sample to shipping"
                      value={sampleLabelToAdd}
                      onChange={(e) => {
                        setSampleLabelToAdd(e.target.value);
                        markAsInteracted();
                      }}
                      fullWidth
                      size="small"
                      placeholder="Sample label"
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'middle' }} />
                  <TableCell sx={{ verticalAlign: 'middle' }} />
                  <TableCell sx={{ verticalAlign: 'middle' }} />
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    <Button variant="outlined" size="small" onClick={handleAddSample}>
                      Add Sample
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isLocalTransfer}
                  onChange={(e) => {
                    setIsLocalTransfer(e.target.checked);
                    if (e.target.checked) setTab(0);
                    markAsInteracted();
                  }}
                />
              }
              label="Local Transfer (instead of shipping)"
            />

            {isLocalTransfer ? (
              <Box>
                <TextField
                  label="Transfer Date"
                  type="date"
                  value={transferDate}
                  onChange={(e) => {
                    setTransferDate(e.target.value);
                    markAsInteracted();
                  }}
                  required
                  fullWidth
                  size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ mb: 2 }}
                />
                <Typography variant="subtitle2" gutterBottom>
                  Comment
                </Typography>
                <RichTextEditor
                  value={comment}
                  onChange={(value) => {
                    setComment(value);
                    markAsInteracted();
                  }}
                />
              </Box>
            ) : null}
          </>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <Button variant="text" onClick={() => setTab(0)} sx={{ alignSelf: 'flex-start', mb: 1 }}>
              ← Back to Select Samples
            </Button>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Paper elevation={0} variant="outlined" sx={{ flex: 1, minWidth: 280, p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Sender
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Internal Order Number"
                    value={budget}
                    onChange={(e) => {
                      setBudget(e.target.value);
                      markAsInteracted();
                    }}
                    required
                    fullWidth
                    size="small"
                  />
                  <ShippingTypeSelect
                    label="Shipping Type"
                    required
                    value={shippingType}
                    options={shippingTypeOptions}
                    onChange={(value) => {
                      setShippingType(value);
                      markAsInteracted();
                    }}
                  />
                  <TextField
                    label="Phone"
                    value={researcherPhone}
                    onChange={(e) => {
                      setResearcherPhone(e.target.value);
                      markAsInteracted();
                    }}
                    required
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Return Address"
                    value={returnAddress}
                    onChange={(e) => {
                      setReturnAddress(e.target.value);
                      markAsInteracted();
                    }}
                    multiline
                    minRows={4}
                    required
                    fullWidth
                    size="small"
                    placeholder="Enter return address for the shipment"
                  />
                </Stack>
              </Paper>
              <Paper elevation={0} variant="outlined" sx={{ flex: 1, minWidth: 280, p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Ship To
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Contact Name"
                    value={contactName}
                    onChange={(e) => {
                      setContactName(e.target.value);
                      markAsInteracted();
                    }}
                    required
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Contact Email"
                    value={contactEmail}
                    onChange={(e) => {
                      setContactEmail(e.target.value);
                      markAsInteracted();
                    }}
                    required
                    fullWidth
                    size="small"
                    type="email"
                  />
                  <TextField
                    label="Contact Phone"
                    value={contactPhone}
                    onChange={(e) => {
                      setContactPhone(e.target.value);
                      markAsInteracted();
                    }}
                    required
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Shipping Address"
                    value={address}
                    onChange={(e) => {
                      setAddress(e.target.value);
                      markAsInteracted();
                    }}
                    multiline
                    minRows={4}
                    required
                    fullWidth
                    size="small"
                    placeholder="Enter recipient shipping address"
                  />
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Comment
              </Typography>
              <RichTextEditor
                value={comment}
                onChange={(value) => {
                  setComment(value);
                  markAsInteracted();
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </StandardDialog>
  );
};
